import { cloneElement, FC, MouseEvent, ReactElement, ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Box, Flex, StyleProps, Text } from '@bonusly/design-system';
import { useBuildMonarchURL } from '@bonusly/url';

import { useIsRecognize } from '../../hooks/useIsRecognize';

interface AdminLinkProps extends StyleProps {
	children: ReactNode;
	icon: ReactElement;
	to: string;
	onClick?: () => void;
}

export const AdminLink: FC<AdminLinkProps> = ({ children, icon, to, onClick, ...rest }) => {
	const isRecognize = useIsRecognize();
	const monarchURL = useBuildMonarchURL();

	// Track pathname changes
	const [currentPathname, setCurrentPathname] = useState(window.location.pathname);

	useEffect(() => {
		const handleLocationChange = () => {
			setCurrentPathname(window.location.pathname);
		};

		const intervalId = setInterval(handleLocationChange, 500);

		return () => {
			clearInterval(intervalId);
		};
	}, []);

	// Most admin links go to Monarch (app.bonus.ly)
	const isFullUrl = to.match(/^http/);
	const finalUrl = isRecognize && !isFullUrl ? monarchURL(to) : to;
	const isCrossDomain = isRecognize && !isFullUrl;

	// Check if this link is active
	const checkIsActive = () => {
		// Extract pathname from the link URL
		let linkPath = to;

		if (finalUrl.includes('http')) {
			try {
				const urlObj = new URL(finalUrl);
				linkPath = urlObj.pathname;
			} catch (e) {
				linkPath = to;
			}
		}

		return linkPath === currentPathname;
	};

	const isActive = checkIsActive();

	// Handle click for cross-domain navigation
	const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
		onClick?.();
		if (isCrossDomain || isRecognize) {
			e.preventDefault();
			window.location.href = finalUrl;
		}
	};

	return (
		<Link to={finalUrl} onClick={handleClick} style={{ textDecoration: 'none' }}>
			<Box
				bg="transparent"
				borderRadius="sm"
				cursor="pointer"
				h={10}
				listStyleType="none"
				px={3}
				py={2}
				tabIndex={0}
				transitionDuration="normal"
				transitionProperty="common"
				_active={{
					bg: 'bg.subtle',
					_hover: {
						bg: 'bg.soft',
					},
				}}
				_hover={{ bg: 'bg.subtle' }}
				{...(isActive && {
					bg: 'bg.subtle',
					'data-active': true,
				})}
				{...rest}
			>
				<Flex alignItems="center">
					{cloneElement(icon, { fontSize: '2xl', key: 'icon' })}
					<Text as="span" color="gray.700" ml={2} fontSize={50} fontWeight="medium">
						{children}
					</Text>
				</Flex>
			</Box>
		</Link>
	);
};
